/**
 * OsScanItem — OS-INV-12 / OS-INV-18 / MADE-290 / MADE-292
 *
 * Route: /os/scan/:code/:location  (PIN-gated via OsSessionGuard)
 * location ∈ 'working' | 'storeroom'
 *
 * OS-INV-18 — Blind Counting:
 *   Stock levels are hidden from employees by default (showStock = false).
 *   A future story (OS-INV-BACKLOG-A) will pass showStock={true} for manager
 *   PIN sessions so that one prop toggle restores visibility without a refactor.
 *
 * MADE-292 — Count Mode:
 *   When localStorage has an active count session (os_count_mode), OsScanItem
 *   renders in Count Mode:
 *   - Amber page background (#FFFDE7)
 *   - "Recording to: [Session Name]" chip
 *   - All stock values hidden (enforces blind counting — non-negotiable)
 *   - Normal Count / Remove / Add buttons REPLACED by a stepper + "Record Count"
 *   - On submit: POST /api/os/count-sessions/:sessionId/count
 *   - Navigate back to /os/count-mode/ready with lastItemName state
 *
 * UX hierarchy (normal mode):
 *   • "Count" is PRIMARY: the most frequent action. Full-width button → dialog.
 *   • Remove / Add are SECONDARY: step adjustments, less visual weight.
 *
 * Session expiry: any 401 → redirect to /os/pin?next=<current URL>.
 *
 * API contract (OS-INV-11 — locked):
 *   GET  /api/os/materials/:code          (header x-os-session-token)
 *   POST /api/os/materials/:code/events   body { eventType, location, quantity, notes? }
 *   → 201 { code, stock_storeroom, stock_working, current_stock, event }
 *
 * Count Mode API (MADE-291):
 *   GET  /api/os/materials/:code?sessionId=:sessionId   → adds priorCountInSession field
 *   POST /api/os/count-sessions/:sessionId/count
 *        body { itemNumber, location, countedQty }
 *        → 201 { countedItems, totalItems }
 *
 * Props:
 *   showStock (default false) — when true, renders the location stock hero.
 *     Set to true for manager PIN sessions (OS-INV-BACKLOG-A).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useOsSession } from '../hooks/useOsSession';
import { scanDestination } from '../utils/scanDestination';
import { useScanListener } from '../hooks/useScanListener';
import { useCountMode } from '../hooks/useCountMode';
import { useTenant } from '../../contexts/TenantContext';

type Location = 'working' | 'storeroom';
type EventType = 'UPDATE' | 'DEDUCT' | 'INSERT';

interface Material {
  code: string;
  description: string;
  unit: string;
  stock_storeroom: number;
  stock_working: number;
  current_stock: number;
  /** System-assigned item number — required for count session submission. */
  itemNumber?: number;
  /** MADE-292: set when fetched with ?sessionId= — true if already counted this session. */
  priorCountInSession?: boolean;
}

/** OS-INV-18: Props for OsScanItem. */
const SUCCESS_MESSAGES = [
  'Count recorded!',
  'Got it — next item!',
  'Count saved.',
  'Nice work!',
  'Logged!',
  'All good. Keep going.',
];


export interface OsScanItemProps {
  /**
   * When true, renders the location stock hero (storeroom / working / total).
   * Defaults to false (blind-count mode). Pass true for manager PIN sessions
   * (OS-INV-BACKLOG-A) — no other code change required.
   * NOTE: In Count Mode (MADE-292), stock is always hidden regardless of this prop.
   */
  showStock?: boolean;
}

const LOCATION_LABELS: Record<Location, string> = {
  working:   'WORKING INVENTORY',
  storeroom: 'STORE ROOM',
};

export const OsScanItem: React.FC<OsScanItemProps> = ({ showStock = false }) => {
  const { code = '', location = 'working' } = useParams<{ code: string; location: string }>();
  const loc = (LOCATION_LABELS[location as Location] ? location : 'working') as Location;
  const { apiBase } = useTenant();
  const { getSnapshot, clearSession } = useOsSession();
  const { getCountMode, updateCountedItems } = useCountMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── Count Mode state (read once on mount; localStorage-derived) ────────────
  const countMode = getCountMode();
  const isCountMode = Boolean(countMode?.sessionId);

  // ── Scan listener: item URL → navigate; count session URL → enter count mode
  useScanListener(
    useCallback(
      (value: string) => {
        const destination = scanDestination(value);
        if (destination) navigate(destination);
      },
      [navigate],
    ),
    useCallback(
      (sessionId: string) => {
        navigate(`/os/count/${encodeURIComponent(sessionId.toLowerCase())}`);
      },
      [navigate],
    ),
  );

  const [material, setMaterial]   = useState<Material | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading]     = useState(true);

  // ── Normal mode: Update dialog ─────────────────────────────────────────────
  const [updateOpen, setUpdateOpen]     = useState(false);
  const [updateQty, setUpdateQty]       = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError]   = useState<string | null>(null);
  const updateInputRef = useRef<HTMLInputElement>(null);

  // ── Count Mode: stepper ────────────────────────────────────────────────────
  const [countQty, setCountQty]       = useState('');
  const countInputRef = useRef<HTMLInputElement>(null);

  // ── Shared mutation state ──────────────────────────────────────────────────
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [mutating, setMutating] = useState(false);

  const currentPath = `/os/scan/${encodeURIComponent(code)}/${loc}${searchParams.toString() ? `?${searchParams}` : ''}`;

  // ── Session-aware fetch helper ─────────────────────────────────────────────
  const sessionFetch = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const { token } = getSnapshot();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string> ?? {}),
      };
      if (token) headers['x-os-session-token'] = token;
      const res = await fetch(url, { ...options, headers });
      if (res.status === 401) {
        clearSession();
        navigate(`/os/pin?next=${encodeURIComponent(currentPath)}`, { replace: true });
      }
      return res;
    },
    [getSnapshot, clearSession, navigate, currentPath],
  );

  // ── Load item ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    (async () => {
      try {
        // MADE-292: include sessionId in count mode so server returns priorCountInSession
        const qs = isCountMode && countMode?.sessionId
          ? `?sessionId=${encodeURIComponent(countMode.sessionId)}`
          : '';
        const res = await sessionFetch(
          `${apiBase}/materials/${encodeURIComponent(code)}${qs}`,
        );
        if (cancelled) return;
        if (res.status === 404) {
          setLoadError('Item not found.');
          setLoading(false);
          return;
        }
        if (!res.ok) {
          setLoadError('Failed to load item.');
          setLoading(false);
          return;
        }
        const data: Material = await res.json();
        setMaterial(data);
      } catch {
        if (!cancelled) setLoadError('Network error — check your connection.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, apiBase, sessionFetch, isCountMode, countMode?.sessionId]);

  // Auto-focus count input when in Count Mode and material loaded
  useEffect(() => {
    if (isCountMode && material && countInputRef.current) {
      setTimeout(() => countInputRef.current?.focus(), 100);
    }
  }, [isCountMode, material]);

  // ── Normal mode: Post event ────────────────────────────────────────────────
  const postEvent = useCallback(
    async (eventType: EventType, quantity: number) => {
      if (!material) return;
      setMutating(true);
      try {
        const res = await sessionFetch(
          `${apiBase}/materials/${encodeURIComponent(code)}/events`,
          {
            method: 'POST',
            body: JSON.stringify({ eventType, location: loc, quantity }),
          },
        );
        if (res.status === 401) return;
        if (res.status === 400) {
          const body = await res.json().catch(() => ({ error: 'Invalid request' }));
          setToastMsg(`Error: ${body.error ?? 'Invalid request'}`);
          return;
        }
        if (!res.ok) {
          setToastMsg('Mutation failed — try again.');
          return;
        }
        const data = await res.json();
        setMaterial(prev =>
          prev
            ? {
                ...prev,
                stock_storeroom: data.stock_storeroom,
                stock_working: data.stock_working,
                current_stock: data.current_stock,
              }
            : prev,
        );
        const verb =
          eventType === 'UPDATE' ? 'Updated' :
          eventType === 'DEDUCT' ? 'Deducted' :
          'Inserted';
        setToastMsg(`${verb} ${quantity} ${material.unit ?? 'units'}`);
      } catch {
        setToastMsg('Network error — mutation may not have saved.');
      } finally {
        setMutating(false);
      }
    },
    [material, sessionFetch, apiBase, code, loc],
  );

  // ── Count Mode: Record Count ───────────────────────────────────────────────
  const handleRecordCount = useCallback(async () => {
    if (!material || !countMode) return;
    const qty = parseFloat(countQty);
    if (isNaN(qty) || qty < 0) {
      setToastMsg('Enter a valid quantity (0 or more).');
      return;
    }

    setMutating(true);
    try {
      const res = await sessionFetch(
        `${apiBase}/count-sessions/${encodeURIComponent(countMode.sessionId)}/count`,
        {
          method: 'POST',
          body: JSON.stringify({
            itemNumber: material.itemNumber,
            location: loc,
            // MADE-292 fix: backend requires 'actualCount', not 'countedQty'
            actualCount: qty,
            // #1: scan source — 'count_sheet' when QR has ?src=sheet, else 'label'
            scanSource: searchParams.get('src') === 'sheet'
              ? 'count_sheet' : 'label',
          }),
        },
      );
      if (res.status === 401) return;
      // #5: Hard block — item not on this session's count list
      if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        if ((body as { offRoster?: boolean }).offRoster) {
          setToastMsg((body as { error?: string }).error ?? 'This item is not part of this inventory cycle.');
          return; // Stay on item page — do not navigate away
        }
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setToastMsg(
          `Failed to record count: ${(body as { error?: string }).error ?? 'Try again.'}`,
        );
        return;
      }
      // MADE-292 fix: progress is nested under data.session, not top-level
      const data = await res.json() as {
        item: { countedQty: number; status: string; priorCountInSession: boolean };
        session: { countedItems: number; totalItems: number; pct: number; rosterMode?: 'open' | 'fixed' };
      };

      // Update local count state so banner + ready screen show fresh progress
      updateCountedItems(data.session?.countedItems ?? 0, data.session?.totalItems, countMode.sessionId, data.session?.rosterMode);

      // Haptic feedback if available
      if ('vibrate' in navigator) navigator.vibrate(50);

      const successMsg = SUCCESS_MESSAGES[Math.floor(Math.random() * SUCCESS_MESSAGES.length)];
      navigate('/os/count-mode/ready', {
        state: { lastItemName: material.description, successMsg },
      });
    } catch {
      setToastMsg('Network error — count may not have saved. Try again.');
    } finally {
      setMutating(false);
    }
  }, [
    material, countMode, countQty, sessionFetch, apiBase, loc,
    updateCountedItems, navigate, searchParams,
  ]);

  // ── Normal mode: Update dialog ─────────────────────────────────────────────
  const openUpdate = () => {
    setUpdateQty('');
    setUpdateError(null);
    setUpdateOpen(true);
    setTimeout(() => updateInputRef.current?.focus(), 100);
  };

  const submitUpdate = async () => {
    const qty = parseFloat(updateQty);
    if (isNaN(qty) || qty < 0) {
      setUpdateError('Enter a valid number (0 or more).');
      return;
    }
    setUpdateLoading(true);
    setUpdateError(null);
    await postEvent('UPDATE', qty);
    setUpdateLoading(false);
    setUpdateOpen(false);
  };

  // ── Normal mode: Step button ───────────────────────────────────────────────
  const handleStep = (type: 'DEDUCT' | 'INSERT', qty: number) => {
    postEvent(type, qty);
  };

  const locStock = loc === 'working' ? material?.stock_working : material?.stock_storeroom;
  const unitLabel = material?.unit ?? 'units';
  const steps = [1, 5, 10];

  // Back destination: count-mode-ready when in count mode, else /os/scan
  const backDest = isCountMode ? '/os/count-mode/ready' : '/os/scan';

  // ── Render: loading ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100dvh',
          bgcolor: isCountMode ? '#FFFDE7' : '#f4f6fb',
        }}
      >
        <CircularProgress sx={{ color: isCountMode ? '#F57C00' : undefined }} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 3, maxWidth: 480, mx: 'auto', mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{loadError}</Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(backDest)}
        >
          {isCountMode ? 'Back to count' : 'Return to Count Sheet'}
        </Button>
      </Box>
    );
  }

  if (!material) return null;

  // ── Render: Count Mode ─────────────────────────────────────────────────────
  if (isCountMode && countMode) {
    return (
      <Box
        sx={{ minHeight: '100dvh', bgcolor: '#FFFDE7', pb: 6 }}
        data-testid="scan-item-count-mode"
      >
        {/* Top nav — amber accent */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: '#E65100',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <IconButton
            onClick={() => navigate(backDest)}
            size="small"
            sx={{ color: '#fff', mr: 1 }}
            aria-label="Back to scan"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ color: '#fff', flex: 1 }}
          >
            {material.description}
          </Typography>
        </Box>

        <Box sx={{ px: 2, pt: 2, maxWidth: 480, mx: 'auto' }}>
          {/* Recording chip */}
          <Chip
            icon={<AssignmentIcon fontSize="small" />}
            label={`Recording to: ${countMode.sessionName}`}
            sx={{
              mb: 2,
              bgcolor: '#FFF3E0',
              color: '#E65100',
              fontWeight: 700,
              border: '1px solid #F57C00',
              '& .MuiChip-icon': { color: '#F57C00' },
            }}
            data-testid="recording-chip"
          />

          {/* Prior count warning */}
          {material.priorCountInSession && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              data-testid="prior-count-warning"
            >
              Already counted in this session — recording again will update your
              count.
            </Alert>
          )}

          {/* Item meta — code, unit, location ONLY (no stock values) */}
          <Box
            sx={{ bgcolor: '#fff', borderRadius: 3, p: 2, mb: 2, boxShadow: 1 }}
            data-testid="item-meta-card"
          >
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Box>
                <Typography variant="caption" color="text.secondary">Name</Typography>
                <Typography variant="body1" fontWeight={700}>
                  {material.description}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Code</Typography>
                <Typography
                  variant="body1"
                  fontWeight={700}
                  fontFamily="monospace"
                >
                  {material.code}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Unit</Typography>
                <Typography variant="body1" fontWeight={700}>{unitLabel}</Typography>
              </Box>
              {material.itemNumber != null && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Item #</Typography>
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    fontFamily="monospace"
                  >
                    #{String(material.itemNumber).padStart(6, '0')}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">Location</Typography>
                <Typography
                  variant="body1"
                  fontWeight={700}
                  sx={{ color: loc === 'working' ? '#1565c0' : '#2e7d32' }}
                >
                  {LOCATION_LABELS[loc]}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Count question */}
          <Box
            sx={{ bgcolor: '#fff', borderRadius: 3, p: 3, mb: 2, boxShadow: 1 }}
          >
            <Typography
              variant="body1"
              fontWeight={600}
              color="#E65100"
              sx={{ mb: 2, textAlign: 'center' }}
            >
              How many did you count?
            </Typography>

            {/* Stepper: large −/input/+ */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                aria-label={`Decrease quantity`}
                onClick={() =>
                  setCountQty(prev => {
                    const n = parseFloat(prev);
                    return isNaN(n) || n <= 0 ? '0' : String(n - 1);
                  })
                }
                sx={{
                  minWidth: 48,
                  minHeight: 48,
                  width: 56,
                  height: 56,
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  borderColor: '#F57C00',
                  color: '#E65100',
                  '&:hover': { bgcolor: '#FFF3E0', borderColor: '#E65100' },
                  flexShrink: 0,
                }}
                data-testid="stepper-minus"
              >
                −
              </Button>

              <TextField
                inputRef={countInputRef}
                value={countQty}
                onChange={e => {
                  const v = e.target.value;
                  if (v === '' || /^\d*\.?\d*$/.test(v)) setCountQty(v);
                }}
                onKeyDown={e => e.key === 'Enter' && handleRecordCount()}
                inputProps={{
                  inputMode: 'numeric',
                  'aria-label': `Quantity in ${unitLabel}`,
                  style: { fontSize: '2rem', fontWeight: 700, textAlign: 'center' },
                  min: 0,
                }}
                placeholder="0"
                fullWidth
                autoComplete="off"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                data-testid="count-qty-input"
              />

              <Button
                variant="outlined"
                aria-label={`Increase quantity`}
                onClick={() =>
                  setCountQty(prev => {
                    const n = parseFloat(prev);
                    return isNaN(n) ? '1' : String(n + 1);
                  })
                }
                sx={{
                  minWidth: 48,
                  minHeight: 48,
                  width: 56,
                  height: 56,
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  borderColor: '#F57C00',
                  color: '#E65100',
                  '&:hover': { bgcolor: '#FFF3E0', borderColor: '#E65100' },
                  flexShrink: 0,
                }}
                data-testid="stepper-plus"
              >
                +
              </Button>
            </Stack>
          </Box>

          {/* Record Count — full-width amber, prominent */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={mutating || countQty === ''}
            onClick={handleRecordCount}
            sx={{
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 700,
              bgcolor: '#F57C00',
              borderRadius: 2,
              '&:hover': { bgcolor: '#E65100' },
              '&.Mui-disabled': { bgcolor: '#FFCC80', color: '#fff' },
            }}
            data-testid="record-count-button"
          >
            {mutating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Record Count'}
          </Button>
        </Box>

        {/* Success / error toast */}
        <Snackbar
          open={Boolean(toastMsg)}
          autoHideDuration={4000}
          onClose={() => setToastMsg(null)}
          message={toastMsg}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          ContentProps={{ role: 'status', 'aria-live': 'polite' } as React.ComponentProps<typeof Snackbar>['ContentProps']}
        />
      </Box>
    );
  }

  // ── Render: Normal mode ────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: '#f4f6fb', pb: 6 }}>
      {/* Top nav */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: '#001550',
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <IconButton
          onClick={() => navigate('/os/scan')}
          size="small"
          sx={{ color: '#fff', mr: 1 }}
          aria-label="Return to Count Sheet"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ color: '#fff', flex: 1, noWrap: true }}
        >
          {material.description}
        </Typography>
      </Box>

      <Box sx={{ px: 2, pt: 2, maxWidth: 480, mx: 'auto' }}>
        {/* Item meta */}
        <Box sx={{ bgcolor: '#fff', borderRadius: 3, p: 2, mb: 2, boxShadow: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">Name</Typography>
              <Typography variant="body1" fontWeight={700}>{material.description}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Code</Typography>
              <Typography variant="body1" fontWeight={700} fontFamily="monospace">
                {material.code}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Unit</Typography>
              <Typography variant="body1" fontWeight={700}>{unitLabel}</Typography>
            </Box>
            {material.itemNumber != null && (
              <Box>
                <Typography variant="caption" color="text.secondary">Item #</Typography>
                <Typography variant="body1" fontWeight={700} fontFamily="monospace">
                  #{String(material.itemNumber).padStart(6, '0')}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">Location</Typography>
              <Typography
                variant="body1"
                fontWeight={700}
                sx={{ color: loc === 'working' ? '#1565c0' : '#2e7d32' }}
              >
                {LOCATION_LABELS[loc]}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* OS-INV-18: Stock hero — hidden by default. showStock={true} for managers. */}
        {showStock && (
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: 3,
              p: 3,
              mb: 2,
              textAlign: 'center',
              boxShadow: 1,
            }}
            data-testid="stock-hero"
          >
            <Typography
              variant="overline"
              fontWeight={700}
              sx={{
                color: loc === 'working' ? '#1565c0' : '#2e7d32',
                fontSize: '0.75rem',
                letterSpacing: '0.1em',
              }}
            >
              {LOCATION_LABELS[loc]}
            </Typography>
            <Typography
              variant="h1"
              fontWeight={800}
              sx={{ fontSize: '4rem', lineHeight: 1.1, color: '#001550', mt: 0.5 }}
              aria-live="polite"
              aria-label={`${LOCATION_LABELS[loc]} stock: ${locStock} ${unitLabel}`}
              data-testid="stock-quantity"
            >
              {locStock ?? 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">{unitLabel}</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Total (all locations):{' '}
              <strong>{material.current_stock} {unitLabel}</strong>
            </Typography>
          </Box>
        )}

        {/* PRIMARY: Count button */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={mutating}
          onClick={openUpdate}
          sx={{
            mb: 3,
            py: 2,
            fontSize: '1.1rem',
            fontWeight: 700,
            bgcolor: '#001550',
            borderRadius: 2,
            '&:hover': { bgcolor: '#002680' },
          }}
          data-testid="count-button"
        >
          Count
        </Button>

        {/* SECONDARY: Deduct / Insert step buttons */}
        <Box sx={{ bgcolor: '#fff', borderRadius: 3, p: 2, boxShadow: 1 }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Remove
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {steps.map(qty => (
              <Button
                key={`deduct-${qty}`}
                variant="outlined"
                size="small"
                disabled={mutating}
                onClick={() => handleStep('DEDUCT', qty)}
                aria-label={`Remove ${qty} ${unitLabel}`}
                sx={{
                  flex: 1,
                  color: '#c62828',
                  borderColor: '#ef9a9a',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#ffebee', borderColor: '#c62828' },
                }}
                data-testid={`deduct-${qty}`}
              >
                −{qty} {unitLabel}
              </Button>
            ))}
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Add
          </Typography>
          <Stack direction="row" spacing={1}>
            {steps.map(qty => (
              <Button
                key={`insert-${qty}`}
                variant="outlined"
                size="small"
                disabled={mutating}
                onClick={() => handleStep('INSERT', qty)}
                aria-label={`Add ${qty} ${unitLabel}`}
                sx={{
                  flex: 1,
                  color: '#2e7d32',
                  borderColor: '#a5d6a7',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#e8f5e9', borderColor: '#2e7d32' },
                }}
                data-testid={`insert-${qty}`}
              >
                +{qty} {unitLabel}
              </Button>
            ))}
          </Stack>
        </Box>

        {mutating && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>

      {/* Update Count dialog */}
      <Dialog
        open={updateOpen}
        onClose={() => !updateLoading && setUpdateOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Count — {LOCATION_LABELS[loc]}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the new absolute count for this location.
          </Typography>
          <TextField
            inputRef={updateInputRef}
            label={`New quantity (${unitLabel})`}
            type="number"
            inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
            value={updateQty}
            onChange={e => {
              setUpdateQty(e.target.value);
              setUpdateError(null);
            }}
            onKeyDown={e => e.key === 'Enter' && submitUpdate()}
            fullWidth
            error={Boolean(updateError)}
            helperText={updateError}
            InputProps={{
              endAdornment: <InputAdornment position="end">{unitLabel}</InputAdornment>,
            }}
            autoComplete="off"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setUpdateOpen(false)}
            disabled={updateLoading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={submitUpdate}
            variant="contained"
            disabled={updateLoading || !updateQty}
            startIcon={updateLoading ? <CircularProgress size={16} /> : undefined}
            sx={{ bgcolor: '#001550', '&:hover': { bgcolor: '#002680' } }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success / error toast */}
      <Snackbar
        open={Boolean(toastMsg)}
        autoHideDuration={3000}
        onClose={() => setToastMsg(null)}
        message={toastMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={
          { role: 'status', 'aria-live': 'polite' } as React.ComponentProps<
            typeof Snackbar
          >['ContentProps']
        }
      />
    </Box>
  );
};
